import { MAX_FILE_SIZE } from '@/constants/config';
import { formatFileSize } from '@/constants/utils/file_size';

/**
 * Pull size / duration caps off a `validation.file` block (tool field or
 * model parameter). Missing or non-positive values are treated as unset.
 */
export function getFileValidationLimits(fileValidation = {}) {
  const maxSizeBytes = Number(fileValidation?.maxSizeBytes);
  const audioSeconds = Number(fileValidation?.audio?.maxDurationSeconds);
  const videoSeconds = Number(fileValidation?.video?.maxDurationSeconds);
  const maxDurationSeconds = audioSeconds > 0
    ? audioSeconds
    : (videoSeconds > 0 ? videoSeconds : null);
  return {
    maxSizeBytes: maxSizeBytes > 0 ? maxSizeBytes : null,
    maxDurationSeconds: maxDurationSeconds > 0 ? maxDurationSeconds : null,
  };
}

export function formatDurationLimit(seconds) {
  const sec = Number(seconds);
  if (!Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 60) {
    const rounded = Math.round(sec);
    return `${rounded} second${rounded === 1 ? '' : 's'}`;
  }
  const minutes = sec / 60;
  if (Number.isInteger(minutes)) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const rounded = Math.round(minutes * 10) / 10;
  return `${rounded} minutes`;
}

/**
 * Human-readable size + duration hint for upload UIs.
 * Always includes a size cap (model-specific, or the global 50MB default)
 * so users see a number even when the selected model only documents duration.
 */
export function formatFileUploadLimits(
  { maxSizeBytes, maxDurationSeconds } = {},
  { fallbackMaxSizeBytes = MAX_FILE_SIZE } = {},
) {
  const sizeBytes = maxSizeBytes || fallbackMaxSizeBytes || null;
  const parts = [];
  if (sizeBytes) parts.push(`Max ${formatFileSize(sizeBytes)}`);
  if (maxDurationSeconds) {
    const durationLabel = formatDurationLimit(maxDurationSeconds);
    if (durationLabel) parts.push(`up to ${durationLabel}`);
  }
  return parts.join(' · ');
}

export function describeFileLimitViolation(file, { maxSizeBytes, maxDurationSeconds } = {}) {
  if (!file) return null;
  const bytes = Number(file.size ?? file.sizeInBytes) || 0;
  if (maxSizeBytes && bytes > maxSizeBytes) {
    return `File is too large (${formatFileSize(bytes)}). Max ${formatFileSize(maxSizeBytes)}.`;
  }
  const seconds = Number(
    file.duration
    ?? file.metadata?.audio?.duration
    ?? file.metadata?.video?.duration
    ?? file.metadata?.[file.fileType]?.duration
  ) || 0;
  if (maxDurationSeconds && seconds > maxDurationSeconds) {
    return `That clip is too long (${formatDurationLimit(seconds)}). Max ${formatDurationLimit(maxDurationSeconds)}.`;
  }
  return null;
}

/**
 * Best-effort duration probe for a browser File. Native skips this (no
 * HTML media element); callers should still enforce size and let the
 * backend catch duration after metadata extraction.
 */
export function probeMediaDurationSeconds(file) {
  if (typeof document === 'undefined' || !file || typeof URL === 'undefined') {
    return Promise.resolve(null);
  }
  const mime = String(file.type || '');
  const name = String(file.name || file.originalName || '').toLowerCase();
  const isVideo = mime.startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv)$/.test(name);
  const isAudio = mime.startsWith('audio/') || /\.(mp3|wav|m4a|aac|ogg|flac|wma)$/.test(name);
  if (!isAudio && !isVideo) return Promise.resolve(null);

  return new Promise((resolve) => {
    const el = document.createElement(isVideo ? 'video' : 'audio');
    const url = URL.createObjectURL(file);
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
      resolve(Number.isFinite(value) && value > 0 ? value : null);
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(el.duration);
    el.onerror = () => done(null);
    setTimeout(() => done(null), 3000);
    el.src = url;
  });
}
